import React, { useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
    Box,
    Divider,
    Typography
} from "@mui/material";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { useAbortController } from '../../contexts/AbortControllerContext';
import ProjectContext from '../../contexts/ProjectContext';

import CodeEditor from "../custom/CodeEditor";
import ExpandedDropDown from "../dropdown/ExpandedDropDown";
import ExpandedSwitch from "../switch/ExpandedSwitch";
import ExpandedTextField from "../display/ExpandedTextField";
import SecondaryButton from "../buttons/SecondaryButton";
import ChipTextField from "../display/ChipTextField";
import {useFetch} from '../../components/auth/useFetch';


export default function SourceConfig(props){

    const { context, source, sourceProcessingConfig, setSourceProcessingConfig, updateValue, savedUpdate } = props
    const { abortSimilarGroupRequests, registerController, unregisterController } = useAbortController()
    const projectContext = useContext(ProjectContext);
    const fetchWithAuth = useFetch()


    useEffect(() => {
        source && (
            getSourceProcessingConfig()
        )
    }, [source, savedUpdate])

    const getSourceProcessingConfig = () => {
        abortSimilarGroupRequests(`source.config.get`)
        const sourcesConfigController = registerController(`source.config.get.${source}`)
        let sourceConfigURL = `${process.env.REACT_APP_BACKEND_URL}/konsol/sources/config/?source_name=${source}`
        fetchWithAuth(sourceConfigURL, {
            headers:{
                'X-Project-ID': projectContext?.project_db
            },
            signal: sourcesConfigController.signal
        })
            .then(res => res.json())
            .then(res => {
                const processTransform = keyValueToObjectArray((res?.process?.transform ?? {}), 'column', 'code')
                res = updateValue('process.transform', res, processTransform)
                const linkTransform = (res?.link ?? []).map(strategy => {
                    return { ...strategy, transform: keyValueToObjectArray((strategy?.transform ?? {}), 'column', 'code')}
                })
                res = updateValue('link', res, linkTransform)
                setSourceProcessingConfig(res)
            })
            .catch(e => {
                if(e.name !== 'AbortError'){
                    console.error(e);
                }
            })
            .finally(() => unregisterController(sourcesConfigController, `source.config.get.${source}`))
    }

    // Convert {<key>: <value>} to [{keyName: <key>, valueName: <value>}]
    function keyValueToObjectArray(data, keyName, valueName){
        return Object.entries(data).map(([key, value]) => {
            return { [keyName]: key, [valueName]: value }
        })
    }

    // Accept only digits or return 0 if target is null
    const handleNumericChange = (e, key) => {
        if(/^\d*$/.test(e.target.value)){
            setSourceProcessingConfig(prev =>  updateValue(key, prev, Number(e.target.value) || 0))
        }
    }

    return (
        <React.Fragment>
        {Object.entries(sourceProcessingConfig).length > 0 &&
            <Box
                my={3}
                width='100%'
                display='flex'
                flexDirection='column'
            >
                <Box
                    width='100%'
                >
                    <Box
                        m='8px 24px'
                        borderRadius={3}
                        border='1px solid rgb(107, 164, 125)'
                        bgcolor='rgba(255, 255, 255, 0.05)'
                    >
                        <Box
                            p={3}
                        >
                            <Typography variant='h5'>SOURCE DEFINITION</Typography>
                            <Typography>Configure a source and what it means to Konsol</Typography>
                            <ExpandedTextField
                                label='Source Name'
                                value={sourceProcessingConfig?.source ?? ''}
                                onChange={(event) => setSourceProcessingConfig(prev => updateValue('source', prev, event.target.value)) }
                                disabled={false}
                                error={(sourceProcessingConfig?.source ?? '') === '' ? true : false}
                            />
                            <ExpandedTextField
                                label='Source Name (Short)'
                                value={sourceProcessingConfig?.shortname ?? ''}
                                onChange={(event) => setSourceProcessingConfig(prev => updateValue('shortname', prev, event.target.value))}
                                disabled={false}
                                error={(sourceProcessingConfig?.shortname ?? '') === '' ? true : false}
                            />
                            <ExpandedDropDown
                                label='File Type'
                                options={context?.type ?? []}
                                value={sourceProcessingConfig?.type ?? ''}
                                onChange={(event) => setSourceProcessingConfig(prev =>  updateValue('type', prev, event.target.value))}
                                fullWidth
                            />
                            <ExpandedDropDown
                                label='File Structure'
                                options={context?.struct ?? []}
                                value={sourceProcessingConfig?.struct ?? ''}
                                onChange={(event) => setSourceProcessingConfig(prev =>  updateValue('struct', prev, event.target.value))}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </Box>

                <Box
                    width='100%'
                >
                    <Box
                        m='8px 24px'
                        borderRadius={3}
                        border='1px solid rgb(107, 164, 125)'
                        bgcolor='rgba(255, 255, 255, 0.05)'
                    >
                        <Box
                            p={3}
                        >
                            <Typography variant='h5'>FILE PROCESSING DEFINITION</Typography>
                            <Typography>Configure how an uploaded file is to be interpreted</Typography>
                            <Box
                                width='100%'
                            >
                                <ExpandedTextField
                                    label='File Extension'
                                    value={sourceProcessingConfig?.preprocess?.extension ?? ''}
                                    onChange={(event) => setSourceProcessingConfig(prev =>  updateValue('preprocess.extension', prev, event.target.value))}
                                    disabled={false}
                                    error={(sourceProcessingConfig?.preprocess?.extension ?? '') === '' ? true : false}
                                />
                                <ExpandedTextField
                                    label='Column Row Number'
                                    value={sourceProcessingConfig?.preprocess?.column_index ?? 0}
                                    onChange={(event) => handleNumericChange(event, 'preprocess.column_index')}
                                    disabled={false}
                                />
                                <ExpandedTextField
                                    label='Data Row Number'
                                    value={sourceProcessingConfig?.preprocess?.data_index ?? 0}
                                    onChange={(event) => handleNumericChange(event, 'preprocess.data_index')}
                                    disabled={false}
                                />
                                <ExpandedDropDown
                                    label='File Delimiter'
                                    options={context?.delimiter ?? []}
                                    value={sourceProcessingConfig?.preprocess?.delimiter ?? 'comma'}
                                    onChange={(event) => setSourceProcessingConfig(prev =>  updateValue('preprocess.delimiter', prev, event.target.value))}
                                    fullWidth
                                />
                                <ExpandedSwitch
                                    label='Fill Down Columns'
                                    checked={sourceProcessingConfig?.preprocess?.fill_down ?? false}
                                    value={(sourceProcessingConfig?.preprocess?.fill_down ?? false) === true ? 'Fill Down' : '' }
                                    onChange={(event) => setSourceProcessingConfig(prev =>  updateValue('preprocess.fill_down', prev, event.target.checked))}
                                />
                            </Box>
                            {sourceProcessingConfig?.preprocess?.data_index === sourceProcessingConfig?.preprocess?.column_index &&
                            <Box
                                width='100%'
                                my={3}
                            >
                                <Box
                                    my={1}
                                    p={2}
                                    sx={{
                                        border: '1px solid rgba(92, 92, 92, 1)',
                                        borderRadius: 3,
                                        '&:hover': {
                                            border: '1px solid rgba(199, 221, 206, 1)'
                                        },
                                        transition: 'border 0.15s ease-out',
                                    }}
                                >
                                    <Typography
                                        sx={{ fontSize: '15px', fontWeight: 800 }}
                                    >
                                        File Headers
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: '12px', fontWeight: 300 }}
                                    >
                                        {
                                            'Define the headers if they don\'t exist in the file.'
                                        }
                                    </Typography>
                                    <Box
                                        width='100%'
                                        display='flex'
                                        flexDirection='row'
                                        flexWrap='wrap'
                                        alignItems='center'
                                        gap={1}
                                        my={2}
                                    >
                                        <ChipTextField
                                            chips={sourceProcessingConfig?.preprocess?.headers ?? []}
                                            setChips={(data) => setSourceProcessingConfig(prev =>  updateValue('preprocess.headers', prev, data))}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            }
                            <Box
                                width='100%'
                            >
                                <Box
                                    width='100%'
                                    my={3}
                                >
                                    <Box
                                        p={2}
                                        my={1}
                                        sx={{
                                            border: '1px solid rgba(92, 92, 92, 1)',
                                            borderRadius: 3,
                                            '&:hover': {
                                                border: '1px solid rgba(199, 221, 206, 1)'
                                            },
                                            transition: 'border 0.15s ease-out',
                                        }}
                                    >
                                        <Typography
                                            sx={{ fontSize: '15px', fontWeight: 800 }}
                                        >
                                            File Transform
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: '12px', fontWeight: 300 }}
                                        >
                                            {
                                                'Apply python code to the raw file to prepare it for parsing. \
                                                This is usually done in the case of extracting data from \
                                                non-standard parts of the file.'
                                            }
                                        </Typography>
                                        <Box
                                            my={1}
                                        >
                                            {sourceProcessingConfig?.preprocess?.transform !== undefined
                                            ?
                                                <Box
                                                    width='100%'
                                                    height='250px'
                                                    my={3}
                                                    display='flex'
                                                    flexDirection='row'
                                                    alignItems='center'
                                                >
                                                    <CodeEditor
                                                        code={sourceProcessingConfig?.preprocess?.transform ?? ''}
                                                        handleChange={(value) => setSourceProcessingConfig(prev =>  updateValue('preprocess.transform', prev, value))}
                                                    />
                                                    <SecondaryButton
                                                        color='rgba(92, 92, 92, 1)'
                                                        hoverColor='rgba(222, 155, 145, 1)'
                                                        icon={<RemoveCircleOutlineIcon />}
                                                        onClick={() => setSourceProcessingConfig(prev =>  updateValue('preprocess.transform', prev, undefined))}
                                                    />
                                                </Box>
                                            :
                                                <Box
                                                    display='flex'
                                                    justifyContent='center'
                                                    width='100%'
                                                >
                                                    <SecondaryButton
                                                        onClick={() => setSourceProcessingConfig(prev =>  updateValue('preprocess.transform', prev, ''))}
                                                        color='rgba(92, 92, 92, 1)'
                                                        hoverColor='rgba(255, 255, 255, 1)'
                                                        icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                                    />
                                                </Box>
                                            }
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box
                    width='100%'
                >
                    <Box
                        m='8px 24px'
                        borderRadius={3}
                        border='1px solid rgb(107, 164, 125)'
                        bgcolor='rgba(255, 255, 255, 0.05)'
                    >
                        <Box
                            p={3}
                        >
                            <Typography variant='h5'>DATA PROCESSING DEFINITION</Typography>
                            <Typography>Configure how the parsed data is to be transformed</Typography>
                            <Box
                                width='100%'
                                my={3}
                            >
                                <Box
                                    p={2}
                                    my={1}
                                    sx={{
                                        border: '1px solid rgba(92, 92, 92, 1)',
                                        borderRadius: 3,
                                        '&:hover': {
                                            border: '1px solid rgba(199, 221, 206, 1)'
                                        },
                                        transition: 'border 0.15s ease-out',
                                    }}
                                >
                                    <Typography
                                        sx={{ fontSize: '15px', fontWeight: 800 }}
                                    >
                                        Data Filter
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: '12px', fontWeight: 300 }}
                                    >
                                        {
                                            'Filters applied to the data. \
                                            Python\'s Pandas library is used under-the-hood to query the filter.'
                                        }
                                    </Typography>
                                    {(sourceProcessingConfig?.process?.filter ?? []).map((filter, index) =>
                                        <Box
                                            key={index}
                                            my={1}
                                        >
                                            <Box
                                                width='100%'
                                                height='50px'
                                                my={3}
                                                display='flex'
                                                flexDirection='row'
                                                alignItems='center'
                                            >
                                                <CodeEditor
                                                    code={filter}
                                                    handleChange={(value) => setSourceProcessingConfig(prev =>  updateValue(`process.filter.${index}`, prev, value))}
                                                />
                                                <SecondaryButton
                                                    onClick={() => setSourceProcessingConfig(prev =>  updateValue(`process.filter.${index}`, prev, undefined) )}
                                                    color='rgba(92, 92, 92, 1)'
                                                    hoverColor='rgba(222, 155, 145, 1)'
                                                    icon={<RemoveCircleOutlineIcon />}
                                                />
                                            </Box>
                                            {sourceProcessingConfig?.process?.filter?.length === index + 1 &&
                                                <Box
                                                    display='flex'
                                                    justifyContent='center'
                                                    width='100%'
                                                >
                                                    <SecondaryButton
                                                        onClick={() => setSourceProcessingConfig(prev =>  updateValue(`process.filter.${index+1}`, prev, '') )}
                                                        color='rgba(92, 92, 92, 1)'
                                                        hoverColor='rgba(255, 255, 255, 1)'
                                                        icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                                    />
                                                </Box>
                                            }
                                        </Box>
                                    )}
                                    {(sourceProcessingConfig?.process?.filter?.length ?? 0) < 1 &&
                                        <Box
                                            display='flex'
                                            justifyContent='center'
                                            width='100%'
                                            my={1}
                                        >
                                            <SecondaryButton
                                                onClick={() => setSourceProcessingConfig(prev =>  updateValue(`process.filter.0`, prev, '') )}
                                                color='rgba(92, 92, 92, 1)'
                                                hoverColor='rgba(255, 255, 255, 1)'
                                                icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                            />
                                        </Box>
                                    }
                                </Box>
                                <Box
                                    p={2}
                                    my={1}
                                    sx={{
                                        border: '1px solid rgba(92, 92, 92, 1)',
                                        borderRadius: 3,
                                        '&:hover': {
                                            border: '1px solid rgba(199, 221, 206, 1)'
                                        },
                                        transition: 'border 0.15s ease-out',
                                    }}
                                >
                                    <Typography
                                        sx={{ fontSize: '15px', fontWeight: 800 }}
                                    >
                                        Data Transform
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: '12px', fontWeight: 300 }}
                                    >
                                        {
                                            'Transforms applied to the data. \
                                            Any data transformations made here WILL be reflected in the uploaded metadata. \
                                            Transforms are applied per column. \
                                            Refer to the documentation on how to perform data-wide transforms.'
                                        }
                                    </Typography>
                                    {(sourceProcessingConfig?.process?.transform ?? []).map((transform, index) =>
                                    <Box
                                        key={index}
                                        my={1}
                                    >
                                        <Box
                                            width='100%'
                                            height='100px'
                                            my={3}
                                            display='flex'
                                            flexDirection='row'
                                            alignItems='center'
                                        >
                                            <ExpandedTextField
                                                label='Transform Column'
                                                value={transform?.column ?? ''}
                                                onChange={(event) => {
                                                    setSourceProcessingConfig(prev =>  updateValue(`process.transform.${index}.column`, prev, event.target.value))
                                                }}
                                                disabled={false}
                                            />
                                            <CodeEditor
                                                code={transform?.code ?? ''}
                                                handleChange={(value) => setSourceProcessingConfig(prev =>  updateValue(`process.transform.${index}.code`, prev, value))}
                                            />
                                            <SecondaryButton
                                                color='rgba(92, 92, 92, 1)'
                                                hoverColor='rgba(222, 155, 145, 1)'
                                                icon={<RemoveCircleOutlineIcon />}
                                                onClick={() => setSourceProcessingConfig(prev =>  updateValue(`process.transform.${index}`, prev, undefined))}
                                            />
                                        </Box>
                                        {(sourceProcessingConfig?.process?.transform ?? []).length === index + 1 &&
                                            <Box
                                                display='flex'
                                                justifyContent='center'
                                                width='100%'
                                            >
                                                <SecondaryButton
                                                    onClick={() => setSourceProcessingConfig(prev =>  updateValue(`process.transform.${index+1}`, prev, {'column': '', 'code': ''}) )}
                                                    color='rgba(92, 92, 92, 1)'
                                                    hoverColor='rgba(255, 255, 255, 1)'
                                                    icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                                />
                                            </Box>
                                        }
                                    </Box>
                                    )}
                                    {(sourceProcessingConfig?.process?.transform ?? []).length < 1 &&
                                        <Box
                                            display='flex'
                                            justifyContent='center'
                                            width='100%'
                                            my={1}
                                        >
                                            <SecondaryButton
                                                onClick={() => setSourceProcessingConfig(prev =>  updateValue(`process.transform.${(sourceProcessingConfig?.process?.transform ?? []).length}`, prev, {'column': '', 'code': ''}) )}
                                                color='rgba(92, 92, 92, 1)'
                                                hoverColor='rgba(255, 255, 255, 1)'
                                                icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                            />
                                        </Box>
                                    }
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box
                    width='100%'
                >
                    <Box
                        m='8px 24px'
                        borderRadius={3}
                        border='1px solid rgb(107, 164, 125)'
                        bgcolor='rgba(255, 255, 255, 0.05)'
                    >
                        <Box
                            p={3}
                        >
                            <Typography variant='h5'>LINKING STRATEGY DEFINITION</Typography>
                            <Typography>Configure how to link uploaded data with media</Typography>
                        </Box>
                        <Box
                            p={1}
                        >
                            {(sourceProcessingConfig?.link ?? []).map((linkingStrategy, index) => 
                                <Box
                                    key={index}
                                    width='100%'
                                >
                                    <Box
                                        p={2}
                                        my={1}
                                        sx={{
                                            border: '1px solid rgba(92, 92, 92, 1)',
                                            borderRadius: 3,
                                            '&:hover': {
                                                border: '1px solid rgba(199, 221, 206, 1)'
                                            },
                                            transition: 'border 0.15s ease-out',
                                        }}
                                        position='relative'
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                            }}
                                        >
                                            {index > 0 &&
                                                <SecondaryButton
                                                    color='rgba(92, 92, 92, 1)'
                                                    hoverColor='rgba(222, 155, 145, 1)'
                                                    icon={<CancelIcon />}
                                                    onClick={() => setSourceProcessingConfig(prev =>  updateValue(`link.${index}`, prev, undefined) )}
                                                />
                                            }
                                        </Box>
                                        <Typography
                                            sx={{ fontSize: '15px', fontWeight: 800 }}
                                        >
                                            Search Transform
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: '12px', fontWeight: 300 }}
                                        >
                                            {
                                                'Column-wise transforms applied to the data. \
                                                Any data transformations made here WILL NOT be reflected in the uploaded metadata. \
                                                This is a non-destructive process and temporary transforms are applied \
                                                to the data to help Konsol identify what media to link to.'
                                            }
                                        </Typography>
                                        {(linkingStrategy?.transform ?? []).map((transform, ind) => 
                                            <Box
                                                key={ind}
                                                width='100%'
                                            >
                                                <Box
                                                    width='100%'
                                                    height='100px'
                                                    my={3}
                                                    display='flex'
                                                    flexDirection='row'
                                                    alignItems='center'
                                                >
                                                    <ExpandedTextField
                                                        label='Transform Column'
                                                        value={transform?.column ?? ''}
                                                        onChange={(event) => {
                                                            setSourceProcessingConfig(prev =>  updateValue(`link.${index}.transform.${ind}.column`, prev, event.target.value))
                                                        }}
                                                        disabled={false}
                                                    />
                                                    <CodeEditor
                                                        code={transform?.code ?? ''}
                                                        handleChange={(value) => setSourceProcessingConfig(prev =>  updateValue(`link.${index}.transform.${ind}.code`, prev, value))}
                                                    />
                                                    <SecondaryButton
                                                        color='rgba(92, 92, 92, 1)'
                                                        hoverColor='rgba(222, 155, 145, 1)'
                                                        icon={<RemoveCircleOutlineIcon />}
                                                        onClick={() => setSourceProcessingConfig(prev =>  updateValue(`link.${index}.transform.${ind}`, prev, undefined))}
                                                    />
                                                </Box>
                                                {(linkingStrategy?.transform ?? []).length === ind + 1 &&
                                                    <Box
                                                        display='flex'
                                                        justifyContent='center'
                                                        width='100%'
                                                    >
                                                        <SecondaryButton
                                                            onClick={() => setSourceProcessingConfig(prev =>  updateValue(`link.${index}.transform.${ind+1}`, prev, {'column': '', 'code': ''}))}
                                                            color='rgba(92, 92, 92, 1)'
                                                            hoverColor='rgba(255, 255, 255, 1)'
                                                            icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                                        />
                                                    </Box>
                                                }
                                            </Box>
                                        )}
                                        {(linkingStrategy?.transform ?? []).length < 1 &&
                                            <Box
                                                display='flex'
                                                justifyContent='center'
                                                width='100%'
                                                my={1}
                                            >
                                                <SecondaryButton
                                                    onClick={() => setSourceProcessingConfig(prev =>  updateValue(`link.${index}.transform.${(linkingStrategy?.transform ?? []).length}`, prev, {'column': '', 'code': ''}))}
                                                    color='rgba(92, 92, 92, 1)'
                                                    hoverColor='rgba(255, 255, 255, 1)'
                                                    icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                                />
                                            </Box>
                                        }
                                        <Box p={3}>
                                            <Divider sx={{ borderColor: 'rgb(75, 75, 75)' }} />
                                        </Box>
                                        <Typography
                                            sx={{ fontSize: '15px', fontWeight: 800 }}
                                        >
                                            Search Filter
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: '12px', fontWeight: 300 }}
                                        >
                                            {
                                                'A JSON formatted string indicating what key from the file should be used \
                                                to search for media identified by Konsol. \
                                                The key of each item corresponds to what media metadata to link to. \
                                                The value of each item is a template string corresponding to the column in the file to use.'
                                            }
                                        </Typography>
                                        {(linkingStrategy?.search ?? {}) &&  
                                            <Box
                                                width='100%'
                                                height='50px'
                                                my={3}
                                                display='flex'
                                                flexDirection='row'
                                                alignItems='center'
                                            >
                                                <CodeEditor
                                                    code={JSON.stringify((linkingStrategy?.search) ?? '')}
                                                    defaultLanguage='json'
                                                    handleChange={(value) => {
                                                        setSourceProcessingConfig(prev =>  {
                                                            try{
                                                                return updateValue(`link.${index}.search`, prev, JSON.parse(value))
                                                            }
                                                            catch{
                                                                return prev
                                                            }
                                                        })
                                                    }}
                                                />
                                            </Box>
                                        }
                                    </Box>
                                    {(sourceProcessingConfig?.link ?? []).length === index + 1 &&
                                        <Box
                                            width='100%'
                                            display='flex'
                                            justifyContent='center'
                                            my={1}
                                        >
                                            <SecondaryButton
                                                onClick={() => setSourceProcessingConfig(prev =>  updateValue(`link.${index+1}`, prev, { 'search': { [source]: { "$exists": false } } } ))}
                                                color='rgba(92, 92, 92, 1)'
                                                hoverColor='rgba(255, 255, 255, 1)'
                                                icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                            />
                                        </Box>
                                    }
                                </Box>
                            )}
                        </Box>
                        {(sourceProcessingConfig?.link ?? []).length < 1 &&
                            <Box
                                width='100%'
                                display='flex'
                                justifyContent='center'
                                my={1}
                            >
                                <SecondaryButton
                                    onClick={() => setSourceProcessingConfig(prev =>  updateValue(`link.0`, prev, { 'search': { [source]: { "$exists": false } } } ))}
                                    color='rgba(92, 92, 92, 1)'
                                    hoverColor='rgba(255, 255, 255, 1)'
                                    icon={<AddCircleOutlineIcon sx={{ fontSize: '35px' }} />}
                                />
                            </Box>
                        }
                    </Box>
                </Box>
            </Box>
        }
        </React.Fragment>
    )
}